/**
 * RouteCard — signed-in `/` home. Never a map, never a booking form.
 * Renders the rider's active plan (if any) or the empty-state card
 * with the "Repeat last week's trip?" CTA (or "Book a ride" fallback
 * if the rider has no history yet).
 *
 * All quote / route pricing continues to come from the existing
 * booking flow — this component only reads /api/me/commute-summary
 * for identity + a compact `last_route` object used by Repeat.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ArrowRight, Clock, User as UserIcon, RotateCcw } from "lucide-react";

import { api } from "@/shared/lib/api";
import { formatTimeWindow12 } from "@/shared/lib/format";
import { AccountAvatarButton } from "@/apps/rider/components/AccountMorph";

export default function RouteCard() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [lastRoute, setLastRoute] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/commute-summary");
        if (cancelled) return;
        setPlan(data?.plan || null);
        setLastRoute(data?.last_route || null);
      } catch {
        if (!cancelled) { setPlan(null); setLastRoute(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hasPlan = plan?.has_plan;

  // Repeat handler: seeds localStorage with last-route + jumpToPay
  // flag, then navigates to /book. ScheduleSetup consumes the preset,
  // hydrates the wizard fields, auto-locks the tier via getTier(),
  // and mounts BookingSheet with initialWizardStepIdx=2 (Review & Pay).
  const repeat = () => {
    const src = lastRoute || (hasPlan ? {
      pickup_address: plan.pickup_address,
      destination_address: plan.destination_address,
      days: plan.days,
      pickup_window: plan.pickup_window,
      return_window: plan.return_window,
      seats: plan.seats,
      ride_type: plan.ride_type,
      billing_cycle: plan.billing_cycle,
    } : null);
    if (!src) return;
    try {
      localStorage.setItem("link.preset.route", JSON.stringify({
        pickup: src.pickup_address || "",
        destination: src.destination_address || "",
        days: src.days || [],
        pickupWindow: src.pickup_window || undefined,
        returnWindow: src.return_window || undefined,
        seats: Number(src.seats || 1),
        pref: (src.ride_type === "private" ? "private" : "shared"),
        cycle: src.billing_cycle === "payg" ? "payg" : "weekly",
        jumpToPay: true,
        ts: Date.now(),
      }));
    } catch { /* private-mode */ }
    nav("/book");
  };

  const startBook = () => nav("/book");

  return (
    <div
      data-testid="route-card-page"
      data-dusk="1"
      style={{
        minHeight: "100vh",
        background: "var(--dusk-bg)",
        color: "var(--dusk-text)",
      }}
    >
      {/* Card chrome — wordmark left + avatar right. Same node the
          booking-sheet headers use, so opening account from home
          reuses the same avatar→X morph. */}
      <div className="flex items-center justify-between px-5 pt-5">
        <img src="/assets/link-full-logo.svg" alt="Link" style={{ height: 26 }} data-testid="route-card-logo" />
        <AccountAvatarButton testId="route-card-avatar" />
      </div>

      <div style={{ padding: "18px 22px 40px" }}>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 36,
            letterSpacing: "-0.02em",
            color: "var(--dusk-text)",
            marginTop: 8,
          }}
          data-testid="route-card-heading"
        >
          YOUR RIDE
        </h1>

        {loading ? (
          <div
            className="mt-6 rounded-3xl"
            style={{
              background: "var(--dusk-glass)",
              boxShadow: "var(--dusk-glass-border)",
              padding: 24,
              color: "var(--dusk-text-muted)",
            }}
            data-testid="route-card-loading"
          >
            Loading…
          </div>
        ) : hasPlan ? (
          <FilledCard plan={plan} onEdit={() => nav("/book")} />
        ) : (
          <EmptyCard lastRoute={lastRoute} onRepeat={repeat} onBook={startBook} />
        )}
      </div>
    </div>
  );
}

function FilledCard({ plan, onEdit }) {
  return (
    <div
      className="mt-6 rounded-3xl"
      style={{
        background: "var(--dusk-glass)",
        backdropFilter: "var(--dusk-glass-blur)",
        WebkitBackdropFilter: "var(--dusk-glass-blur)",
        boxShadow: "var(--dusk-glass-border)",
        padding: 20,
      }}
      data-testid="route-card-filled"
    >
      <div style={{ color: "var(--dusk-text-muted)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700 }}>
        Your plan
      </div>
      <div className="mt-2 flex items-center gap-2" style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17 }}>
        <MapPin size={16} color="var(--dusk-cta)" />
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
          {plan.pickup_short || plan.pickup_address || "Pickup"}
        </span>
        <ArrowRight size={13} color="var(--dusk-text-muted)" />
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
          {plan.destination_short || plan.destination_address || "Destination"}
        </span>
      </div>

      <div style={{ height: 1, background: "var(--dusk-hairline, rgba(255,255,255,0.08))", margin: "16px 0" }} />

      <Row icon={Clock} label="Pickup" value={plan.pickup_window ? formatTimeWindow12(plan.pickup_window) : (plan.pickup_time || "—")} testId="filled-pickup" />
      <Row icon={Clock} label="Return" value={plan.return_window ? formatTimeWindow12(plan.return_window) : (plan.return_time || "—")} testId="filled-return" />
      <Row icon={UserIcon} label="Seats" value={String(plan.seats || 1)} testId="filled-seats" />

      <button
        type="button"
        onClick={onEdit}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full"
        style={{
          background: "rgba(255,255,255,0.06)",
          color: "var(--dusk-text)",
          padding: "14px 20px",
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 14,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)",
        }}
        data-testid="route-card-edit"
      >
        Edit plan <ArrowRight size={14} />
      </button>
    </div>
  );
}

function EmptyCard({ lastRoute, onRepeat, onBook }) {
  return (
    <div
      className="mt-6 rounded-3xl"
      style={{
        background: "var(--dusk-glass)",
        backdropFilter: "var(--dusk-glass-blur)",
        WebkitBackdropFilter: "var(--dusk-glass-blur)",
        boxShadow: "var(--dusk-glass-border)",
        padding: 22,
      }}
      data-testid="route-card-empty"
    >
      <div style={{ color: "var(--dusk-text-muted)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700 }}>
        This week
      </div>
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          fontSize: 22,
          lineHeight: 1.2,
          marginTop: 10,
          color: "var(--dusk-text)",
        }}
        data-testid="empty-headline"
      >
        You don’t have a ride this week.
      </div>
      <div
        style={{ color: "var(--dusk-text-muted)", fontSize: 15, marginTop: 6 }}
        data-testid="empty-subhead"
      >
        {lastRoute ? "Repeat last week’s trip?" : "Book your first weekly route."}
      </div>

      {lastRoute && (
        <div
          className="mt-4 rounded-2xl flex items-center gap-2"
          style={{
            background: "rgba(255,255,255,0.05)",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
            padding: "12px 14px",
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: 14,
            color: "var(--dusk-text)",
          }}
          data-testid="empty-last-route"
        >
          <MapPin size={14} color="var(--dusk-cta)" />
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>
            {(lastRoute.pickup_address || "").split(",")[0] || "Pickup"}
          </span>
          <ArrowRight size={12} color="var(--dusk-text-muted)" />
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 140 }}>
            {(lastRoute.destination_address || "").split(",")[0] || "Destination"}
          </span>
        </div>
      )}

      {lastRoute ? (
        <button
          type="button"
          onClick={onRepeat}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full"
          style={{
            background: "#ed6500",
            color: "#fff",
            padding: "16px 20px",
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 16,
            boxShadow: "0 8px 24px rgba(237,101,0,0.35)",
          }}
          data-testid="route-card-repeat"
        >
          <RotateCcw size={16} /> Repeat
        </button>
      ) : (
        <button
          type="button"
          onClick={onBook}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full"
          style={{
            background: "#ed6500",
            color: "#fff",
            padding: "16px 20px",
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 16,
            boxShadow: "0 8px 24px rgba(237,101,0,0.35)",
          }}
          data-testid="route-card-book"
        >
          Book a ride <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div
      className="flex items-center gap-3 py-2"
      data-testid={testId}
    >
      <Icon size={16} color="var(--dusk-cta)" strokeWidth={1.9} />
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, color: "var(--dusk-text)" }}>{label}</div>
        <div style={{ color: "var(--dusk-text-muted)", fontSize: 13 }}>{value}</div>
      </div>
    </div>
  );
}
